// Tools > Game Files: look bundles up in the game's file list (kept by the
// processing service) and fetch the chosen ones into a new workspace, which is
// then opened like uploaded files (main.js openGameFiles).
import { $, escapeHtml, formatByteSize } from "./util.js";

export const GAME_SERVERS = {
  global: { label: "Global", platforms: ["android", "ios"] },
  japan: { label: "Japan", platforms: ["android", "ios", "windows"] },
};

export const PLATFORM_LABELS = { android: "Android", ios: "iOS", windows: "Windows" };

/** How a pattern is matched; the keys are the methods the Worker accepts (bagfd's filter methods). */
export const METHOD_LABELS = {
  auto: "Auto-detect",
  contains: "Contains",
  glob: "Wildcards (* ?)",
  regex: "Regular expression",
  starts_with: "Starts with",
  ends_with: "Ends with",
};

const LAST_KEY = "abvme-game-target";

/** The platforms a server has files for (Global has no Windows build). */
export const platformsOf = (server) => GAME_SERVERS[server]?.platforms ?? [];

/** "5 minutes ago": how long before `nowSec` the time `thenSec` (both epoch seconds) was. */
export function agoText(thenSec, nowSec = Date.now() / 1000) {
  const s = Math.max(0, nowSec - thenSec);
  const unit = (n, word) => `${n} ${word}${n === 1 ? "" : "s"} ago`;
  if (s < 60) return "just now";
  if (s < 3600) return unit(Math.floor(s / 60), "minute");
  if (s < 86400) return unit(Math.floor(s / 3600), "hour");
  return unit(Math.floor(s / 86400), "day");
}

/** The note under the list: when the file list was last checked against the game and last changed. */
export function catalogNote(row, nowSec = Date.now() / 1000) {
  if (!row?.checked_at) return "";
  const version = row.version ? `File list version ${row.version} · ` : "File list ";
  const changed = row.changed_at ? ` · last changed ${agoText(row.changed_at, nowSec)}` : "";
  return `${version}checked ${agoText(row.checked_at, nowSec)}${changed}`;
}

/** The name a row shows: for Japan the file's path inside the ZIP packs, for Global the file name. */
export const rowLabel = (file) => (file.pack ? file.path || file.name : file.name);

/** What a result row says beside the name: Global the size and folder; Japan the pack that is downloaded for it. */
export function describeFile(file) {
  if (file.pack) return `in ${file.pack} (${formatByteSize(file.pack_size)})`;
  const folder = file.path && file.path.includes("/") ? file.path.slice(0, file.path.lastIndexOf("/") + 1) : "";
  return `${formatByteSize(file.size ?? 0)}${folder ? ` · ${folder}` : ""}`;
}

/** The line under the list: how many are chosen, and a hint when that is more than one fetch may bring. */
export function selectionSummary(files, maxFiles) {
  if (!files.length) return "Nothing selected.";
  const over = files.length > maxFiles ? ` — at most ${maxFiles} at once` : "";
  return `${files.length} selected${over}`;
}

function remember(server, platform) {
  try {
    localStorage.setItem(LAST_KEY, JSON.stringify({ server, platform }));
  } catch {
    // Private window or blocked storage: the choice is just not kept.
  }
}

function lastTarget() {
  try {
    const t = JSON.parse(localStorage.getItem(LAST_KEY) || "null");
    if (t && platformsOf(t.server).includes(t.platform)) return t;
  } catch {
    // Fall through to the default.
  }
  return { server: "global", platform: "android" };
}

/**
 * The dialog. `deps` comes from main.js:
 *   search(server, platform, pattern, method, matchCase)   the Worker's answer: {count, truncated, files, method, checked_at, changed_at}
 *   catalog()                           when each file list was checked: {targets: [{server, platform, checked_at, changed_at, ...}]}
 *   maxFiles()                          most files one fetch may bring in
 *   start({server, platform, files, pattern})   fetch and open the chosen files; `pattern` is the text last searched for
 *   openModal(el, opts) / closeModal(el)
 */
export function createGameFiles(deps) {
  const dlg = $("#gameFilesDialog");
  const server = $("#gameServer");
  const platform = $("#gamePlatform");
  const method = $("#gameMethod");
  const catalogEl = $("#gameCatalog");
  const pattern = $("#gamePattern");
  const searchBtn = $("#gameSearch");
  const caseBtn = $("#gameCase");
  const list = $("#gameList");
  const note = $("#gameNote");
  const summary = $("#gameSummary");
  const openBtn = $("#gameOpen");
  /** Chosen files by ref; kept while the same server and platform are searched again. */
  let chosen = new Map();
  let shown = [];
  /** Set once a search has been run, so that switching Match case runs it again. */
  let listed = false;
  /** The text of the last search, which names the workspace the chosen files open in. */
  let searched = "";
  /** The row last clicked, where a Shift+click range starts. */
  let anchor = -1;
  /** A mouse drag over the rows in progress: {from, base, moved, x, y, frame}. */
  let drag = null;
  /** Set when a drag ended, so the click that follows it does not choose again. */
  let dragged = false;
  /** What the list says when it has no rows (HTML). */
  let emptyHtml = "";
  let busy = false;
  /** The file lists' check times, by "server-platform". */
  let catalogRows = new Map();

  function fillPlatforms(keep) {
    const options = platformsOf(server.value);
    platform.innerHTML = options.map((p) => `<option value="${p}">${PLATFORM_LABELS[p]}</option>`).join("");
    platform.value = options.includes(keep) ? keep : options[0];
  }

  function renderCatalog() {
    catalogEl.textContent = catalogNote(catalogRows.get(`${server.value}-${platform.value}`));
  }

  async function loadCatalog() {
    try {
      const status = await deps.catalog();
      catalogRows = new Map(status.targets.map((t) => [`${t.server}-${t.platform}`, t]));
    } catch {
      // The note is a nicety: without it the dialog works the same.
    }
    renderCatalog();
  }

  function render() {
    list.innerHTML = shown.length
      ? shown
          .map((f, i) => {
            const on = chosen.has(f.ref);
            return `<div class="item${on ? " selected" : ""}" role="option" aria-selected="${on}" data-i="${i}" tabindex="0">
              <span class="name" title="${escapeHtml(rowLabel(f))}">${escapeHtml(rowLabel(f))}</span>
              <span class="meta" title="${escapeHtml(describeFile(f))}">${escapeHtml(describeFile(f))}</span></div>`;
          })
          .join("")
      : `<div class="empty">${emptyHtml}</div>`;
    paintFooter();
  }

  function paintFooter() {
    const files = [...chosen.values()];
    summary.textContent = selectionSummary(files, deps.maxFiles());
    openBtn.disabled = busy || !files.length || files.length > deps.maxFiles();
    $("#gameSelectAll").disabled = busy || !shown.length;
  }

  /** Show the chosen rows without rebuilding the list, so a drag keeps its scroll position and rows. */
  function paint() {
    for (const el of list.querySelectorAll(".item")) {
      const on = chosen.has(shown[Number(el.dataset.i)]?.ref);
      el.classList.toggle("selected", on);
      el.setAttribute("aria-selected", String(on));
    }
    paintFooter();
  }

  const setEmpty = (html) => {
    shown = [];
    emptyHtml = html;
    render();
  };

  async function runSearch() {
    const text = pattern.value.trim();
    if (!text || busy) return;
    searched = text;
    busy = true;
    listed = true;
    searchBtn.disabled = true;
    note.textContent = "";
    setEmpty(`<div class="spin mx-auto mb-2"></div>Searching…`);
    try {
      const result = await deps.search(server.value, platform.value, text, method.value, caseBtn.getAttribute("aria-pressed") === "true");
      shown = result.files;
      anchor = -1;
      const row = catalogRows.get(`${server.value}-${platform.value}`);
      if (result.checked_at) catalogRows.set(`${server.value}-${platform.value}`, { ...row, version: result.version, checked_at: result.checked_at, changed_at: result.changed_at });
      const how = method.value === "auto" && result.method ? ` Matched as ${METHOD_LABELS[result.method].toLowerCase()}.` : "";
      if (!shown.length) {
        setEmpty("No file matches.");
      } else {
        note.textContent = (result.truncated ? `${result.count.toLocaleString()} files match; the first ${shown.length} are listed. Narrow the search to see the rest.` : `${result.count.toLocaleString()} file${result.count === 1 ? "" : "s"} found.`) + how;
      }
    } catch (err) {
      setEmpty(escapeHtml(err.message));
    } finally {
      busy = false;
      searchBtn.disabled = false;
      render();
    }
  }

  /**
   * Choosing rows works like the app's other lists: a click chooses just that row, Ctrl/Cmd+click
   * adds or removes it, Shift+click chooses the range from the last row clicked (with Ctrl/Cmd, adds
   * it). On a touch screen a tap adds or removes, since there is no Ctrl key.
   */
  function choose(i, e = {}) {
    const f = shown[i];
    if (!f) return;
    const add = e.ctrlKey || e.metaKey;
    if (e.shiftKey && anchor >= 0 && shown[anchor]) {
      if (!add) chosen = new Map();
      for (let k = Math.min(i, anchor); k <= Math.max(i, anchor); k++) chosen.set(shown[k].ref, shown[k]);
    } else if (add || matchMedia("(pointer: coarse)").matches) {
      if (chosen.has(f.ref)) chosen.delete(f.ref);
      else chosen.set(f.ref, f);
      anchor = i;
    } else {
      chosen = new Map([[f.ref, f]]);
      anchor = i;
    }
    render();
  }

  /** The index of the row under (x, y), keeping the pointer inside the list so a drag past its edge still lands on a row. */
  function rowAt(x, y) {
    const box = list.getBoundingClientRect();
    const px = Math.min(Math.max(x, box.left + 2), box.right - 2);
    const py = Math.min(Math.max(y, box.top + 2), box.bottom - 2);
    const item = document.elementFromPoint(px, py)?.closest?.(".item");
    return item && list.contains(item) ? Number(item.dataset.i) : -1;
  }

  /** Choose the rows from where the drag began to the one under the pointer (plus the earlier picks when Ctrl/Cmd was held). */
  function dragSelect() {
    const to = rowAt(drag.x, drag.y);
    if (to < 0) return;
    if (to !== drag.from) drag.moved = true;
    if (!drag.moved) return;
    chosen = new Map(drag.base);
    for (let k = Math.min(drag.from, to); k <= Math.max(drag.from, to); k++) chosen.set(shown[k].ref, shown[k]);
    anchor = drag.from;
    paint();
  }

  /** While the pointer is held near the top or bottom edge, keep scrolling the list and extending the choice. */
  function dragScroll() {
    if (!drag) return;
    const box = list.getBoundingClientRect();
    const edge = 24;
    const speed = drag.y < box.top + edge ? -(box.top + edge - drag.y) : drag.y > box.bottom - edge ? drag.y - (box.bottom - edge) : 0;
    if (speed) {
      list.scrollTop += Math.max(-24, Math.min(24, speed / 2 || Math.sign(speed)));
      dragSelect();
    }
    drag.frame = requestAnimationFrame(dragScroll);
  }

  function dragMove(e) {
    if (!drag) return;
    drag.x = e.clientX;
    drag.y = e.clientY;
    dragSelect();
  }

  function dragEnd() {
    if (!drag) return;
    cancelAnimationFrame(drag.frame);
    window.removeEventListener("pointermove", dragMove);
    window.removeEventListener("pointerup", dragEnd);
    window.removeEventListener("pointercancel", dragEnd);
    dragged = drag.moved;
    drag = null;
    // The click that ends a drag arrives straight after the pointer-up; only that one is skipped.
    if (dragged) setTimeout(() => (dragged = false), 0);
  }

  /** A mouse press on a row starts a drag; releasing on the same row is a plain click, handled by `choose`. */
  function dragStart(e) {
    dragged = false;
    if (e.pointerType !== "mouse" || e.button !== 0 || e.shiftKey || busy) return;
    const item = e.target.closest(".item");
    if (!item) return;
    drag = {
      from: Number(item.dataset.i),
      base: e.ctrlKey || e.metaKey ? new Map(chosen) : new Map(),
      moved: false,
      x: e.clientX,
      y: e.clientY,
      frame: requestAnimationFrame(dragScroll),
    };
    window.addEventListener("pointermove", dragMove);
    window.addEventListener("pointerup", dragEnd);
    window.addEventListener("pointercancel", dragEnd);
  }

  function start() {
    const files = [...chosen.values()];
    if (!files.length || files.length > deps.maxFiles()) return;
    remember(server.value, platform.value);
    deps.closeModal(dlg);
    deps.start({ server: server.value, platform: platform.value, files, pattern: searched });
  }

  const resetResults = () => {
    chosen = new Map();
    shown = [];
    anchor = -1;
    listed = false;
    note.textContent = "";
    setEmpty("Type part of a file name, then Search. With Auto-detect, * and ? make it a wildcard pattern and ^ $ \\ + | ( a regular expression. Upper and lower case are treated alike unless Match case (the A button) is on.");
    renderCatalog();
  };

  server.addEventListener("change", () => {
    fillPlatforms(platform.value);
    resetResults();
  });
  platform.addEventListener("change", resetResults);
  searchBtn.addEventListener("click", runSearch);
  pattern.addEventListener("keydown", (e) => e.key === "Enter" && (e.preventDefault(), runSearch()));
  list.addEventListener("pointerdown", dragStart);
  list.addEventListener("click", (e) => {
    if (dragged) return;
    const item = e.target.closest(".item");
    if (item) choose(Number(item.dataset.i), e);
  });
  list.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
      e.preventDefault();
      chosen = new Map(shown.map((f) => [f.ref, f]));
      render();
    } else if (e.key === " " || e.key === "Enter") {
      const item = e.target.closest(".item");
      if (!item) return;
      e.preventDefault();
      choose(Number(item.dataset.i), { ctrlKey: true }); // the keyboard has no click modifiers: add or remove
    }
  });
  $("#gameSelectAll").addEventListener("click", () => {
    const all = shown.every((f) => chosen.has(f.ref));
    for (const f of shown) {
      if (all) chosen.delete(f.ref);
      else chosen.set(f.ref, f);
    }
    render();
  });
  openBtn.addEventListener("click", start);
  // Match case: off (ignore case) until switched on; a list that is showing is searched again.
  caseBtn.addEventListener("click", () => {
    const on = caseBtn.getAttribute("aria-pressed") !== "true";
    caseBtn.setAttribute("aria-pressed", String(on));
    caseBtn.title = on ? "Match case: on" : "Match case: off";
    $("img", caseBtn).src = on ? "/icons/match-case.svg" : "/icons/match-case-off.svg";
    if (pattern.value.trim() && listed) runSearch();
  });
  method.innerHTML = Object.entries(METHOD_LABELS).map(([value, label]) => `<option value="${value}">${label}</option>`).join("");

  /** Show the dialog (on the last server and platform used). */
  function show() {
    const last = lastTarget();
    server.value = last.server;
    fillPlatforms(last.platform);
    resetResults();
    deps.openModal(dlg, { onShown: () => pattern.focus() });
    loadCatalog();
  }

  return { show };
}
